import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCourse, getPracticeRecords } from '../api/supabaseAPI';
import PracticeReportModal from './PracticeReportModal';
import DiagnosisReport from './DiagnosisReport'; // ✨ Import DiagnosisReport
import Gauge from './Gauge'; 
import '../styles/CourseProgressModal.css';

export default function CourseProgressModal({ isOpen, onClose, courseId, studentId }) {
  const [selectedReport, setSelectedReport] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  const [selectedDiagnosisData, setSelectedDiagnosisData] = useState(null); // Renamed for clarity
  const [isDiagnosisModalOpen, setIsDiagnosisModalOpen] = useState(false);

  // 1. Fetch Course Details (for sentences)
  const { data: course, isLoading: isLoadingCourse } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => getCourse(courseId),
    enabled: !!courseId && isOpen,
  });

  // 2. Fetch Student Practice Records
  const { data: records, isLoading: isLoadingRecords } = useQuery({
    queryKey: ['practiceRecords', studentId, course?.language],
    queryFn: () => getPracticeRecords(studentId, course.language),
    enabled: !!studentId && !!course?.language && isOpen,
  });

  // 3. Merge Data
  const courseItems = useMemo(() => {
    if (!course || !records) return [];

    const contentList = course.course_contents?.content || [];
    
    // Map content to items
    return contentList.map((sentence, index) => {
      const sentenceIndex = index + 1; // 1-based index for display
      const expectedDiffiLevel = `Course${courseId}_${sentenceIndex}`;
      
      // Find matching record (get the latest one if multiple)
      const matchingRecords = records.filter(r => r.diffi_level === expectedDiffiLevel);
      // Sort by created_at desc to get latest
      matchingRecords.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      const latestRecord = matchingRecords[0];

      return {
        id: sentenceIndex,
        sentence: sentence,
        record: latestRecord || null,
        status: latestRecord ? 'completed' : 'pending',
        score: latestRecord ? (100 - (latestRecord.error_rate * 100)) : 0
      };
    });
  }, [course, records, courseId]);

  if (!isOpen) return null;

  return (
    <>
      <div className="course-progress-overlay" onClick={onClose}>
        <div className="course-progress-modal" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="course-progress-header">
            <button className="course-progress-back" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h2 className="course-progress-title">
              {isLoadingCourse ? 'Loading Course...' : (course?.course_name || 'Course Progress')}
            </h2>
            <div style={{ width: 24 }}></div> {/* Spacer for centering */}
          </div>

          {/* Content Grid */}
          <div className="course-progress-content">
            {(isLoadingCourse || isLoadingRecords) ? (
               <div className="course-progress-loading">Loading progress...</div>
            ) : (
              <div className="course-progress-grid">
                {courseItems.map((item) => (
                  <div key={item.id} className={`course-item-card ${item.status}`}>
                    <div className="course-item-top">
                      <span className="course-item-index">#{item.id}</span>
                      {item.record && (
                      <div className="course-item-gauge">
                        <Gauge value={item.score} size="small" showValue={false} />
                        <span className={`course-item-score ${
                            item.score >= 66 ? 'good' : 
                            item.score >= 33 ? 'ok' : 'bad'
                        }`}>
                          {Math.round(item.score)}%
                        </span>
                      </div>
                      )}
                    </div>
                    
                    <div className="course-item-middle">
                      <p className="course-item-sentence" title={item.sentence}>
                        {item.sentence}
                      </p>
                    </div>

                    <div className="course-item-bottom">
                      {item.record ? (
                        <button 
                          className="course-item-btn view-report"
                          onClick={() => {
                            setSelectedDiagnosisData({
                              full_log: item.record.full_log,
                              full_report: item.record.full_report
                            });
                            setIsDiagnosisModalOpen(true);
                          }}
                        >
                          View Result
                        </button>
                      ) : (
                        <span className="course-item-status">Not Started</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✨ Corrected: Custom Modal for DiagnosisReport */}
      {isDiagnosisModalOpen && selectedDiagnosisData && (
        <div className="course-progress-overlay" onClick={() => setIsDiagnosisModalOpen(false)} style={{ zIndex: 2300 }}>
          <div className="course-progress-modal" 
               onClick={e => e.stopPropagation()}
               style={{ maxWidth: '600px', height: 'auto', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
             <div className="course-progress-header">
                <button className="course-progress-back" onClick={() => setIsDiagnosisModalOpen(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Close
                </button>
                <h3 className="course-progress-title">Pronunciation Result</h3>
                <div style={{ width: 60 }}></div>
             </div>
             
             <div className="course-progress-content" style={{ overflowY: 'auto' }}>
                <DiagnosisReport 
                  result={selectedDiagnosisData.full_log} 
                  nextAction={
                    selectedDiagnosisData.full_report && Object.keys(selectedDiagnosisData.full_report).length > 0 ? (
                      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                        <button 
                          className="course-item-btn view-report"
                          style={{ 
                            width: '100%', 
                            justifyContent: 'center', 
                            background: 'var(--primary-color)', 
                            color: 'white',
                            padding: '12px',
                            fontSize: '1rem'
                          }}
                          onClick={() => {
                            setSelectedReport(selectedDiagnosisData.full_report);
                            setIsReportModalOpen(true);
                          }}
                        >
                          View AI Analysis Report
                        </button>
                      </div>
                    ) : null
                  }
                />
             </div>
          </div>
        </div>
      )}

      <PracticeReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        report={selectedReport}
      />
    </>
  );
}
